/* eslint-disable consistent-return */
import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import _isEmpty from 'lodash/isEmpty';
import i18n from '../../lib/reacti18n';
import { isCustomChatBotPromptConfig, isNodeChatAlreadyExists } from '../../lib/globalHelper';
import { trackPage, trackLink } from '../../lib/analytics-helper';
import { initializeChatBot } from '../../lib/chat-bot-live-helper';
import { elmoTreatmentNames, elmoExperimentResourceNames } from '../../lib/experiments-helper';
import Page from '../../lib/page';
import CustomChatBotContainer from './customChatBotContainer';
import * as AsyncHelper from '../../lib/asyncMessageHelper';
import { asyncOrigins, bannerColors } from '../commonConstants';
import { getElmoExperiments } from '../../home/actions/action-app';

const CustomChatBot = (props) => {
  const dispatch = useDispatch();

  const { pageName, faqID, banner, articleContent } = props;

  useEffect(() => {
    if (banner === bannerColors.WHITE_BANNER) {
      dispatch(getElmoExperiments(elmoExperimentResourceNames.whiteBannerChatBotResourceName));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const customChatBotConfig = isCustomChatBotPromptConfig(faqID);

  const elmoTreatments = useSelector((state) => state.app.elmoTreatments);

  const isPartOfWhiteBannerChatBotTreatment = elmoTreatments.indexOf(elmoTreatmentNames.whiteBannerChatBotExp) >= 0;

  const isAsync = useSelector((state) => state.postChatBotEligibility.chatBotAsync);

  const checkActiveConv = useSelector((state) => state.postChatBotEligibility.mmtResponse);

  // Setting up custom ChatBot Heading/Description/Button text from props 'banner'
  const getCustomChatBotHeading = () => {
    if (banner === bannerColors.BLUE_BANNER) {
      return i18n.message('smarthelp/chatBot.customChat.Heading');
    }
    if (banner === bannerColors.WHITE_BANNER) {
      return i18n.message('smarthelp/chatBot.customWhiteChat.Heading');
    }
    return null;
  };

  const getCustomChatBotDescription = () => {
    if (banner === bannerColors.BLUE_BANNER) {
      return i18n.message(`smarthelp/chatBot.customChat.Description.${customChatBotConfig.Tcode}`);
    }
    if (banner === bannerColors.WHITE_BANNER) {
      return i18n.message('smarthelp/chatBot.customWhiteChat.Description');
    }
    return null;
  };

  const getCustomChatBotButton = () => {
    if (banner === bannerColors.BLUE_BANNER) {
      return i18n.message('smarthelp/chatBot.customChat.Button');
    }
    if (banner === bannerColors.WHITE_BANNER) {
      return i18n.message('smarthelp/chatBot.customWhiteChat.Button');
    }
    return null;
  };

  // ChatBot Button onClick handler
  const onClickButtonHandler = () => {
    let chatBotParams;
    if (!isNodeChatAlreadyExists()) {
      const articleTitle = articleContent?.articleTitle || '';
      if (banner === bannerColors.BLUE_BANNER) {
        trackLink({
          id: `customChatBotClicked${pageName}`,
          hpid: faqID,
          link: 'custom-chatbot-clicked',
        });
        chatBotParams = {
          topicRefId: customChatBotConfig.Tcode,
          link: 'smarthelp-chat-bot-article',
          hpid: faqID,
          subject: articleTitle,
        };
      }
      if (banner === bannerColors.WHITE_BANNER) {
        trackLink({
          id: `customChatBotClicked${pageName}`,
          link: 'custom-chatbot-clicked',
        });
        chatBotParams = {
          topicRefId: 'GENPOOL',
          link: 'smarthelp-chat-bot-home',
          origin: asyncOrigins.HOME_TRAY,
          subject: articleTitle,
        };
      }
      if (isAsync) {
        return AsyncHelper.initiateAsyncChat(chatBotParams);
      }
      return initializeChatBot(chatBotParams);
    }
    return null;
  };

  // Rendering custom ChatBot container
  const rendercustomChatBotPrompt = () => {
    if (banner === bannerColors.BLUE_BANNER) {
      trackPage({ id: `customChatBotShown${pageName}`, hpid: faqID });
    }
    if (banner === bannerColors.WHITE_BANNER) {
      trackPage({ id: `customChatBotShown${pageName}` });
    }
    return (
      <CustomChatBotContainer
        banner={banner}
        // eslint-disable-next-line react/jsx-no-bind
        chatBotonClickButtonHandler={onClickButtonHandler}
        faqID={faqID}
        headingLabel={getCustomChatBotHeading()}
        descriptionLabel={getCustomChatBotDescription()}
        buttonLabel={getCustomChatBotButton()}
      />
    );
  };

  // Checking if ChatBot is eligible for display
  const isChatBotEnable = () => {
    const LoggedIn = Page.isLoggedIn();
    if (!LoggedIn) {
      return false;
    }
    if (banner === bannerColors.BLUE_BANNER) {
      return (
        !_isEmpty(customChatBotConfig) &&
        customChatBotConfig.isCustomChatBotPromptEnabled &&
        customChatBotConfig.Tcode &&
        !checkActiveConv.isConversationActive
      );
    }
    if (banner === bannerColors.WHITE_BANNER) {
      return isPartOfWhiteBannerChatBotTreatment && !checkActiveConv.isConversationActive;
    }
  };

  const renderCustomChatBot = () => {
    if (isChatBotEnable()) {
      return rendercustomChatBotPrompt();
    }
    return null;
  };
  return renderCustomChatBot();
};

CustomChatBot.prototype = {
  pageName: PropTypes.string.isRequired,
  faqID: PropTypes.string.isRequired,
  banner: PropTypes.string.isRequired,
};

export default React.memo(CustomChatBot);
